/* =========================================================
   pd-forms.js — shared form logic for Pharmaco Diabetes forms
   Handles: field validation, cart operations, Shopify contact-form
   submission, AddressFinder (via app proxy), success/error states.

   Free SKU variant IDs (NZ store — pharmaco-diabetes-nz.myshopify.com)
   These are $0 ACTIVE products not linked from any navigation.
   ========================================================= */

const PD_FORMS = (() => {
  // -----------------------------------------------------------
  // NZ Free variant IDs (set by Shopify product creation script)
  // -----------------------------------------------------------
  const VARIANT_IDS = {
    'FREE-METER-N-POP':  '53348812063085',
    'FREE-METER-N':      '53348812194157',
    'FREE-SKIN-A':       '53348812226925',
    'FREE-SKIN-C':       '53348812259693',
    'FREE-SKIN-POP':     '53348812292461',
    'FREE-SKIN-PREM-A':  '53348812325229',
    'FREE-SKIN-PREM-B':  '53348812390765',
    'FREE-SKIN-DUAL-B':  '53348812423533',
    'FREE-LANCETS':      '53348812456301',
  };

  // Skin SKU lookup: radio value → free SKU
  // Form 1 skins
  const FORM1_SKIN_MAP = {
    'PI642013-A': 'FREE-SKIN-A',
    'PI642013-C': 'FREE-SKIN-C',
    'PI642013-D': 'FREE-SKIN-POP',
  };
  // Form 1 meters
  const FORM1_METER_MAP = {
    'PI643496-FOC': 'FREE-METER-N-POP',
    'PI643212-FOC': 'FREE-METER-N',
  };
  // Form 2 skins
  const FORM2_SKIN_MAP = {
    'PI640000050': 'FREE-SKIN-PREM-A',
    'PI640000051': 'FREE-SKIN-PREM-B',
    'PI640000069': 'FREE-SKIN-DUAL-B',
    'PI642013-A':  'FREE-SKIN-A',
    'PI642013-C':  'FREE-SKIN-C',
    'PI642013-D':  'FREE-SKIN-POP',
  };

  // Free shipping is handled by a Shopify shipping profile assigned only to the $0 free products.
  // Carts containing those products use that profile's $0 rate — no discount code needed.

  // -----------------------------------------------------------
  // Validation helpers
  // -----------------------------------------------------------
  function showFieldError(input, message) {
    clearFieldError(input);
    input.classList.add('pd-page-form__input--error');
    const err = document.createElement('span');
    err.className = 'pd-page-form__field-error';
    err.setAttribute('role', 'alert');
    err.textContent = message;
    input.parentNode.appendChild(err);
  }

  function clearFieldError(input) {
    input.classList.remove('pd-page-form__input--error');
    const existing = input.parentNode.querySelector('.pd-page-form__field-error');
    if (existing) existing.remove();
  }

  function validateRequired(input, label, message) {
    if (!input?.value.trim()) {
      showFieldError(input, message || `${label} is required.`);
      return false;
    }
    clearFieldError(input);
    return true;
  }

  function validateEmail(input) {
    const val = input.value.trim();
    if (!val) {
      showFieldError(input, 'Email address is required.');
      return false;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val)) {
      showFieldError(input, 'Please enter a valid email address.');
      return false;
    }
    clearFieldError(input);
    return true;
  }

  // NZ/AU phone: landline or mobile, with optional country code
  function validatePhone(input, required) {
    const val = input.value.trim();
    if (!val && !required) {
      clearFieldError(input);
      return true;
    }
    if (!val && required) {
      showFieldError(input, 'Phone number is required.');
      return false;
    }
    // Strips spaces/dashes/parens then checks digit count
    const digits = val.replace(/[\s\-().+]/g, '');
    if (!/^\d{7,12}$/.test(digits)) {
      showFieldError(input, 'Please enter a valid phone number.');
      return false;
    }
    clearFieldError(input);
    return true;
  }

  // Production coupon pattern: year prefix 18–35, four digits, four letters.
  const COUPON_PATTERN = /^(1[8-9]|2[0-9]|3[0-5])-[0-9]{4}[A-Za-z]{4}$/;

  function validateCoupon(input, { requiredMessage, invalidMessage } = {}) {
    const val = input.value.trim();
    if (!val) {
      showFieldError(input, requiredMessage || 'Coupon code is required.');
      return false;
    }
    if (!COUPON_PATTERN.test(val)) {
      showFieldError(input, invalidMessage || 'Coupon Code is not valid. It must be in the correct format.');
      return false;
    }
    clearFieldError(input);
    return true;
  }

  function validateRadioGroup(name, formEl, label) {
    const radios = formEl.querySelectorAll(`input[type="radio"][name="${name}"]`);
    const checked = [...radios].some(r => r.checked);
    const container = radios[0]?.closest('.pd-page-form__meter-grid, .pd-page-form__skin-grid');
    const existing = container?.querySelector('.pd-page-form__field-error');
    if (!checked) {
      if (container && !existing) {
        const err = document.createElement('span');
        err.className = 'pd-page-form__field-error';
        err.setAttribute('role', 'alert');
        err.textContent = `Please choose a ${label}.`;
        container.after(err);
      }
      return false;
    }
    if (existing) existing.remove();
    return true;
  }

  function validateCheckbox(input, message) {
    if (!input.checked) {
      showFieldError(input, message);
      return false;
    }
    clearFieldError(input);
    return true;
  }

  // -----------------------------------------------------------
  // Success / error state
  // -----------------------------------------------------------
  function showSuccess(formEl, name, message) {
    const successEl = formEl.closest('section, div[id]')
      ?.querySelector('.pd-page-form__success');
    if (successEl) {
      const nameEl = successEl.querySelector('.pd-page-form__success-name');
      if (nameEl) nameEl.textContent = name || '';
      if (message) successEl.querySelector('.pd-page-form__success-message').textContent = message;
      successEl.removeAttribute('hidden');
      successEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
    formEl.setAttribute('hidden', '');
  }

  function ensureErrorBanner(formEl) {
    let banner = formEl.querySelector('.pd-page-form__error-banner');
    if (!banner) {
      banner = document.createElement('div');
      banner.className = 'pd-page-form__error-banner';
      banner.setAttribute('role', 'alert');
      banner.innerHTML = `<p class="pd-page-form__error-banner-text"></p>
        <button type="button" class="pd-page-form__error-retry">Try again</button>`;
      banner.querySelector('.pd-page-form__error-retry')
        .addEventListener('click', () => banner.setAttribute('hidden', ''));
      formEl.prepend(banner);
    }
    return banner;
  }

  function showFormError(formEl, message) {
    const banner = ensureErrorBanner(formEl);
    banner.classList.remove('pd-page-form__error-banner--validation');
    banner.querySelector('.pd-page-form__error-banner-text').textContent = message;
    banner.removeAttribute('hidden');
    banner.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  function clearValidationSummary(formEl) {
    const banner = formEl.querySelector('.pd-page-form__error-banner--validation');
    if (banner) banner.setAttribute('hidden', '');
  }

  function showValidationSummary(formEl) {
    const banner = ensureErrorBanner(formEl);
    banner.classList.add('pd-page-form__error-banner--validation');
    banner.querySelector('.pd-page-form__error-banner-text').textContent =
      'You’ve missed a required field. Please check the errors below and try again.';
    banner.removeAttribute('hidden');
    banner.scrollIntoView({ behavior: 'smooth', block: 'center' });

    const firstErrorInput = formEl.querySelector('.pd-page-form__input--error');
    const firstErrorMsg = formEl.querySelector('.pd-page-form__field-error');
    if (firstErrorInput) {
      try { firstErrorInput.focus({ preventScroll: true }); } catch (_) { /* ignore */ }
    } else if (firstErrorMsg) {
      firstErrorMsg.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }

  function setSubmitBusy(btn, busy) {
    btn.disabled = busy;
    btn.textContent = busy ? 'Submitting…' : btn.dataset.label || 'Submit';
  }

  // -----------------------------------------------------------
  // Shopify AJAX Cart API
  // -----------------------------------------------------------
  async function addToCart(variantIds) {
    const items = variantIds.map(id => ({ id, quantity: 1 }));
    const resp = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items }),
    });
    if (!resp.ok) {
      const body = await resp.json().catch(() => ({}));
      // Shopify's reason (e.g. a free product sold out) is customer-safe, so keep it for the banner.
      const error = new Error(body.description || 'Could not add items to cart.');
      error.userFacing = true;
      throw error;
    }
    return resp.json();
  }

  async function clearCart() {
    const resp = await fetch('/cart/clear.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
    });
    if (!resp.ok) {
      throw new Error('Could not clear cart.');
    }
    return resp.json();
  }

  // Free shipping is applied automatically by the dedicated Shopify shipping profile — no code needed.
  // Prefill params must go on /cart/checkout (or a cart permalink) — not bare /checkout.
  function buildCheckoutPrefillUrl(checkout = {}) {
    const params = new URLSearchParams();

    Object.entries(checkout).forEach(([key, value]) => {
      if (value) params.set(key, value);
    });

    const query = params.toString();
    return query ? `/cart/checkout?${query}` : '/cart/checkout';
  }

  function clearCheckoutCache() {
    try {
      ['__ui', 'checkout_token'].forEach((key) => localStorage.removeItem(key));
    } catch (_) {
      // Ignore storage access errors (private browsing, etc.)
    }
  }

  // -----------------------------------------------------------
  // Set cart note + attributes (passes form data to the order)
  // -----------------------------------------------------------
  async function setCartAttributes(attributes, note) {
    const body = {};
    if (attributes) body.attributes = attributes;
    if (note)       body.note = note;

    const resp = await fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });

    if (!resp.ok) {
      throw new Error('Failed to save form details to cart');
    }

    return resp.json();
  }

  function getInputValue(form, selector) {
    return form.querySelector(selector)?.value?.trim() || '';
  }

  function setInputValue(form, selector, value) {
    const input = form.querySelector(selector);
    if (input && value) input.value = value;
  }

  function firstMetaValue(metaData, keys) {
    if (!metaData) return '';

    for (const key of keys) {
      if (metaData[key]) return metaData[key];
    }

    return '';
  }

  function checkoutPrefill({ email, firstName, lastName, phone, address1, city, province, zip, country }) {
    return {
      'checkout[email]': email,
      'checkout[shipping_address][first_name]': firstName,
      'checkout[shipping_address][last_name]': lastName,
      'checkout[shipping_address][phone]': phone,
      'checkout[shipping_address][address1]': address1,
      'checkout[shipping_address][city]': city,
      'checkout[shipping_address][province]': province,
      'checkout[shipping_address][zip]': zip,
      'checkout[shipping_address][country]': country,
    };
  }

  // -----------------------------------------------------------
  // Pharmaco Forms API (Render) — customer upsert + marketing opt-in
  // -----------------------------------------------------------
  // Prefer Shopify app proxy (HMAC-signed). Fall back to Render /public/forms when
  // the app proxy is not configured on the installed app (same pattern as AddressFinder).
  const FORMS_API_PROXY = '/apps/pharmaco-forms';
  const FORMS_API_PUBLIC = 'https://pharmaco-forms-api.onrender.com/public/forms';

  async function submitFormsApi(endpoint, payload) {
    const urls = [
      `${FORMS_API_PROXY}/${endpoint}`,
      `${FORMS_API_PUBLIC}/${endpoint}`,
    ];
    let lastError = null;

    for (const url of urls) {
      try {
        const resp = await fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify(payload),
        });
        const contentType = resp.headers.get('content-type') || '';
        if (!contentType.includes('json')) {
          lastError = new Error('Forms API unavailable');
          continue;
        }
        const data = await resp.json();
        if (!resp.ok) {
          const detail = data.detail;
          const message = typeof detail === 'string'
            ? detail
            : Array.isArray(detail) && detail[0]?.msg
              ? detail[0].msg
              : 'Registration failed';
          throw new Error(message);
        }
        return data;
      } catch (err) {
        lastError = err;
      }
    }

    throw lastError || new Error('Forms API unavailable');
  }

  function newPatientApiPayload(formData) {
    return {
      firstName: formData.firstName,
      lastName: formData.lastName,
      email: formData.email,
      phone: formData.phone,
      address: formData.address,
      address1: formData.address1,
      address2: formData.address2,
      city: formData.city,
      region: formData.region,
      postcode: formData.postcode,
      country: 'NZ',
      coupon: formData.coupon,
      meterChoice: formData.meterVal,
      skinChoice: formData.skinVal,
      doableOptIn: Boolean(formData.doable),
    };
  }

  function registerDoableApiPayload(formData) {
    return {
      firstName: formData.firstName,
      lastName: formData.lastName,
      email: formData.email,
      phone: formData.phone || null,
      country: 'NZ',
      meterSerial: formData.serial,
      skinChoice: formData.skinVal,
      doableOptIn: true,
    };
  }

  function registerMeterApiPayload(formData) {
    return {
      firstName: formData.firstName,
      lastName: formData.lastName,
      email: formData.email,
      phone: formData.phone || null,
      address: formData.address,
      country: 'AU',
      meterModel: formData.meterModel,
      meterSerial: formData.meterSerial || null,
    };
  }

  // -----------------------------------------------------------
  // AddressFinder autocomplete
  // -----------------------------------------------------------
  // The AddressFinder widget always requests `{base}/api/{country}/address`.
  // Our Forms API exposes `/api/{country}/address/autocomplete` (and metadata),
  // so a dummy widget key of "proxy" was being sent to AddressFinder and shown
  // as "Key not Found". Drive our own list against the proxy instead.
  // API failures fail silently so the address can still be typed by hand.
  const ADDRESSFINDER_API_BASE = 'https://pharmaco-forms-api.onrender.com/public';

  function addressFinderUrls(country, suffix, params) {
    const cc = String(country || 'AU').toLowerCase();
    const query = new URLSearchParams(params).toString();
    return [
      `${FORMS_API_PROXY}/api/${cc}/address/${suffix}?${query}`,
      `${ADDRESSFINDER_API_BASE}/api/${cc}/address/${suffix}?${query}`,
    ];
  }

  async function fetchAddressFinderJson(urls) {
    for (const url of urls) {
      try {
        const resp = await fetch(url, { headers: { Accept: 'application/json' } });
        const contentType = resp.headers.get('content-type') || '';
        if (!contentType.includes('json')) continue;
        const data = await resp.json();
        if (data && data.success !== false) return data;
      } catch (_) { /* try next */ }
    }
    return null;
  }

  function completionLabel(item) {
    return item?.a || item?.full_address || item?.address || '';
  }

  function initAddressFinder({ inputId, country, onSelect }) {
    const input = document.getElementById(inputId);
    if (!input) return;

    const field = input.closest('.pd-page-form__field') || input.parentElement;
    field.classList.add('pd-page-form__field--address');

    const list = document.createElement('ul');
    list.className = 'pd-address-suggest';
    list.hidden = true;
    list.setAttribute('role', 'listbox');
    field.appendChild(list);

    let items = [];
    let activeIndex = -1;
    let debounceTimer = null;

    function hideList() {
      list.hidden = true;
      list.innerHTML = '';
      items = [];
      activeIndex = -1;
    }

    function setActive(index) {
      activeIndex = index;
      list.querySelectorAll('.pd-address-suggest__item').forEach((el, i) => {
        el.classList.toggle('is-active', i === index);
      });
    }

    async function choose(item) {
      hideList();
      const label = completionLabel(item);
      const id = item.pxid || item.id || item.canonical_address_id || '';
      let meta = item;
      if (id) {
        const data = await fetchAddressFinderJson(
          addressFinderUrls(country, 'metadata', { id, pxid: id })
        );
        if (data) meta = data.metadata || data.address || data;
      }
      if (onSelect) onSelect(label, meta);
      else if (label) input.value = label;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }

    function render(completions) {
      items = completions.filter(completionLabel);
      if (!items.length) {
        hideList();
        return;
      }
      list.innerHTML = '';
      items.forEach((item) => {
        const li = document.createElement('li');
        li.className = 'pd-address-suggest__item';
        li.setAttribute('role', 'option');
        li.textContent = completionLabel(item);
        li.addEventListener('mousedown', (e) => {
          e.preventDefault();
          choose(item);
        });
        list.appendChild(li);
      });
      setActive(0);
      list.hidden = false;
    }

    async function search(query) {
      if (query.trim().length < 3) {
        hideList();
        return;
      }
      const data = await fetchAddressFinderJson(
        addressFinderUrls(country, 'autocomplete', { q: query.trim() })
      );
      const completions = data?.completions || data?.results || [];
      if (!Array.isArray(completions)) {
        hideList();
        return;
      }
      render(completions);
    }

    input.setAttribute('autocomplete', 'off');
    input.setAttribute('aria-autocomplete', 'list');

    input.addEventListener('input', () => {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => search(input.value), 200);
    });

    input.addEventListener('keydown', (e) => {
      if (list.hidden || !items.length) return;
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setActive(Math.min(activeIndex + 1, items.length - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActive(Math.max(activeIndex - 1, 0));
      } else if (e.key === 'Enter' && activeIndex >= 0) {
        e.preventDefault();
        choose(items[activeIndex]);
      } else if (e.key === 'Escape') {
        hideList();
      }
    });

    input.addEventListener('blur', () => {
      setTimeout(hideList, 150);
    });
  }

  // -----------------------------------------------------------
  // Form 1: NZ New Patient Free Meter
  // -----------------------------------------------------------
  function initForm1() {
    const form = document.getElementById('newPatientForm');
    if (!form) return;

    const btn = form.querySelector('.pd-page-form__submit');
    if (btn) btn.dataset.label = btn.textContent;

    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      let valid = true;

      const firstNameInput = form.querySelector('#npf-first-name');
      const lastNameInput = form.querySelector('#npf-last-name');
      const emailInput = form.querySelector('#npf-email');
      const couponInput = form.querySelector('#npf-coupon');

      valid = validateRequired(firstNameInput, 'First name') && valid;
      valid = validateRequired(lastNameInput, 'Last name') && valid;
      if (emailInput.value.trim()) {
        valid = validateEmail(emailInput) && valid;
      } else {
        showFieldError(emailInput, 'The EmailAddress field is required.');
        valid = false;
      }
      valid = validateCoupon(couponInput, {
        requiredMessage: 'The CouponCode field is required.',
        invalidMessage: 'Coupon Code is not valid. It must be in the correct format.',
      }) && valid;
      valid = validateRadioGroup('SelectedMeter', form, 'meter') && valid;
      valid = validateRadioGroup('SelectedSkin', form, 'skin') && valid;

      if (!valid) {
        showValidationSummary(form);
        return;
      }
      clearValidationSummary(form);

      setSubmitBusy(btn, true);
      const doable = form.querySelector('#npf-doable')?.checked;

      try {
        const firstName  = firstNameInput.value.trim();
        const lastName   = lastNameInput.value.trim();
        const fullName   = `${firstName} ${lastName}`.trim();
        const email      = emailInput.value.trim();
        const coupon     = couponInput.value.trim();
        const meterVal   = form.querySelector('input[name="SelectedMeter"]:checked')?.value;
        const skinVal    = form.querySelector('input[name="SelectedSkin"]:checked')?.value;

        const meterSku = FORM1_METER_MAP[meterVal];
        const skinSku  = FORM1_SKIN_MAP[skinVal];

        if (!meterSku || !skinSku) {
          showFormError(form, 'Invalid selection — please choose a meter and a skin.');
          setSubmitBusy(btn, false);
          return;
        }

        const formData = {
          firstName,
          lastName,
          email,
          phone: null,
          address: '',
          address1: '',
          address2: '',
          city: '',
          region: '',
          postcode: '',
          coupon,
          meterVal,
          skinVal,
          doable,
        };

        // Doable opt-in → create/update Shopify customer with email marketing consent (Klaviyo sync).
        if (doable) {
          await submitFormsApi('new-patient', newPatientApiPayload(formData));
        }

        // These flows must generate $0 orders only. Clear any existing paid cart
        // contents before adding the selected free-form products.
        await clearCart();

        const variantIds = [VARIANT_IDS[meterSku], VARIANT_IDS[skinSku]];

        await addToCart(variantIds);
        await setCartAttributes({
          'Customer Name': fullName,
          'Email':         email,
          'Coupon Code':   coupon,
          'Meter Choice':  meterVal,
          'Skin Choice':   skinVal,
          'Doable Opt-In': doable ? 'Yes' : 'No',
          'Form':          'NZ New Patient Free Meter',
        });

        clearCheckoutCache();
        window.location.href = buildCheckoutPrefillUrl(checkoutPrefill({
          email,
          firstName,
          lastName,
          country: 'NZ',
        }));
      } catch (err) {
        console.error('[pd-forms Form1]', err);
        const message = err?.userFacing
          ? `${err.message} Please contact us and we'll sort it out.`
          : doable
            ? 'We could not complete your Doable registration. Please try again or contact us.'
            : 'Something went wrong. Please try again or contact us.';
        showFormError(form, message);
        setSubmitBusy(btn, false);
      }
    });
  }

  // -----------------------------------------------------------
  // Form 2: NZ Register for Doable
  // -----------------------------------------------------------
  function initForm2() {
    const form = document.getElementById('doableForm');
    if (!form) return;

    const btn = form.querySelector('.pd-page-form__submit');
    if (btn) btn.dataset.label = btn.textContent;

    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      let valid = true;

      valid = validateRequired(form.querySelector('#drf-first-name'), 'First name') && valid;
      valid = validateRequired(form.querySelector('#drf-last-name'), 'Last name')   && valid;
      valid = validateEmail(form.querySelector('#drf-email'))                        && valid;
      valid = validatePhone(form.querySelector('#drf-phone'), false)                && valid;
      valid = validateRequired(form.querySelector('#drf-serial'), 'Meter serial number') && valid;
      valid = validateRadioGroup('SelectedSkin', form, 'skin')                     && valid;
      valid = validateCheckbox(form.querySelector('#drf-legal'),
        'Please confirm the acknowledgement to continue.')                          && valid;

      if (!valid) {
        showValidationSummary(form);
        return;
      }
      clearValidationSummary(form);

      setSubmitBusy(btn, true);

      try {
        const firstName = form.querySelector('#drf-first-name').value.trim();
        const lastName  = form.querySelector('#drf-last-name').value.trim();
        const email     = form.querySelector('#drf-email').value.trim();
        const phone     = form.querySelector('#drf-phone')?.value.trim();
        const serial    = form.querySelector('#drf-serial').value.trim();
        const skinVal   = form.querySelector('input[name="SelectedSkin"]:checked')?.value;

        const skinSku = FORM2_SKIN_MAP[skinVal];
        if (!skinSku) {
          showFormError(form, 'Invalid skin selection — please choose a skin.');
          setSubmitBusy(btn, false);
          return;
        }

        await submitFormsApi('register-doable', registerDoableApiPayload({
          firstName,
          lastName,
          email,
          phone,
          serial,
          skinVal,
        }));

        // These flows must generate $0 orders only. Clear any existing paid cart
        // contents before adding the selected free-form products.
        await clearCart();

        const variantIds = [VARIANT_IDS[skinSku], VARIANT_IDS['FREE-LANCETS']];

        await addToCart(variantIds);
        await setCartAttributes({
          'Customer Name':       `${firstName} ${lastName}`,
          'Email':               email,
          'Meter Serial Number': serial,
          'Skin Choice':         skinVal,
          'Phone':               phone || '',
          'Form':                'NZ Register for Doable',
        });

        clearCheckoutCache();
        window.location.href = buildCheckoutPrefillUrl(checkoutPrefill({
          email,
          firstName,
          lastName,
          phone,
          country: 'NZ',
        }));
      } catch (err) {
        console.error('[pd-forms Form2]', err);
        showFormError(form, err?.userFacing
          ? `${err.message} Please contact us and we'll sort it out.`
          : 'We could not complete your Doable registration. Please try again or contact us.');
        setSubmitBusy(btn, false);
      }
    });
  }

  // -----------------------------------------------------------
  // Form 3: AU Register Your Meter
  // -----------------------------------------------------------
  function initForm3() {
    const form = document.getElementById('registerMeterForm');
    if (!form) return;

    const btn = form.querySelector('.pd-page-form__submit');
    if (btn) btn.dataset.label = btn.textContent;

    if (document.getElementById('rmf-address')) {
      initAddressFinder({
        inputId: 'rmf-address',
        country: 'AU',
      });
    }

    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      let valid = true;

      valid = validateRequired(form.querySelector('#rmf-first-name'), 'First name')  && valid;
      valid = validateRequired(form.querySelector('#rmf-last-name'), 'Last name')    && valid;
      valid = validateEmail(form.querySelector('#rmf-email'))                         && valid;
      valid = validatePhone(form.querySelector('#rmf-phone'), false)                 && valid;
      valid = validateRequired(form.querySelector('#rmf-address'), 'Address')        && valid;
      valid = validateRequired(form.querySelector('#rmf-meter-model'), 'Meter model') && valid;

      if (!valid) {
        showValidationSummary(form);
        return;
      }
      clearValidationSummary(form);

      setSubmitBusy(btn, true);

      try {
        const firstName   = form.querySelector('#rmf-first-name').value.trim();
        const lastName    = form.querySelector('#rmf-last-name').value.trim();
        const email       = form.querySelector('#rmf-email').value.trim();
        const phone       = form.querySelector('#rmf-phone')?.value.trim();
        const address     = form.querySelector('#rmf-address').value.trim();
        const meterModel  = form.querySelector('#rmf-meter-model').value.trim();
        const meterSerial = form.querySelector('#rmf-meter-serial')?.value.trim();

        await submitFormsApi('register-meter', registerMeterApiPayload({
          firstName,
          lastName,
          email,
          phone,
          address,
          meterModel,
          meterSerial,
        }));

        form.closest('.pd-page-form')?.classList.add('pd-page-form--submitted');
        showSuccess(form, firstName, null);
        setSubmitBusy(btn, false);
      } catch (err) {
        console.error('[pd-forms Form3]', err);
        showFormError(form, 'We could not register your meter. Please try again or contact us.');
        setSubmitBusy(btn, false);
      }
    });
  }

  // -----------------------------------------------------------
  // Init on DOMContentLoaded
  // -----------------------------------------------------------
  document.addEventListener('DOMContentLoaded', () => {
    initForm1();
    initForm2();
    initForm3();
  });

  return { VARIANT_IDS };
})();
